import { useState, useCallback, useRef, DragEvent, useEffect } from "react";
import { Play, CheckCircle } from "lucide-react";
import FileText from "../../icons/Draganddrop.svg?react";
import PolicyIcon from "../../icons/Policy.svg?react";
import TechStackIcon from "../../icons/TechStack.svg?react";
import InventoryIcon from "../../icons/Inventory.svg?react";
import ArtifactIcon from "../../icons/Artifact.svg?react";
import "./style.scss";
import PolicyCard from "./PolicyCard";
import StepCard from "./StepCard";
import StepDropZone from "./StepDropZone";
import PolicySelectionModal from "./PolicySelectionModal";
// ===== TYPES =====
export interface Policy {
  id: string;
  name: string;
  description: string;
  icon: string;
  iconColor: "orange" | "blue" | "teal" | "purple";
}

export interface AuditStep {
  id: string;
  policies: Policy[];
  responseCheck: boolean;
  preSendReview: boolean;
}

interface AuditSequencingProps {
  policies: Policy[];
  onSequenceUpdate?: (steps: AuditStep[]) => void;
  initialSteps?: AuditStep[];
}

// ===== ICON RENDERER UTILITY =====
function renderIconByName(iconName: string) {
  switch (iconName) {
    case "Policy":
      return <PolicyIcon className="icon-svg" />;
    case "TechStack":
      return <TechStackIcon className="icon-svg" />;
    case "Inventory":
      return <InventoryIcon className="icon-svg" />;
    case "Artifact":
      return <ArtifactIcon className="icon-svg" />;
    default:
      return <span>{iconName}</span>;
  }
}

// ===== MAIN COMPONENT =====
function AuditSequencing({
  policies,
  onSequenceUpdate,
  initialSteps,
}: AuditSequencingProps) {
  const [steps, setSteps] = useState<AuditStep[]>(initialSteps || []);
  const [isCanvasOver, setIsCanvasOver] = useState(false);
  const [hoverStepIndex, setHoverStepIndex] = useState<number | null>(null);
  const [hoverAddIndex, setHoverAddIndex] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalStepNumber, setModalStepNumber] = useState(0);
  const [modalStepIndex, setModalStepIndex] = useState(-1);
  const stepCounterRef = useRef(0);
  const canvasDragCounter = useRef(0);
  const isInitializedRef = useRef(false);
  const previousInitialStepsRef = useRef<string>("");
  const [stepHeights, setStepHeights] = useState<{ [key: number]: number }>({});
  const canvasRef = useRef<HTMLDivElement>(null);
  const autoScrollAnimationRef = useRef<number | null>(null);
  const handleStepHeight = (idx: number, height: number) => {
    setStepHeights(prev => ({ ...prev, [idx]: height }));
  };

  // Get the first step's dimensions to use for all placeholders
  const getStepDimensions = () => {
    if (steps.length > 0 && stepHeights[0]) {
      return {
        width: 320, // Fixed width from CSS
        height: stepHeights[0]
      };
    }
    return { width: 320, height: 180 }; // Default fallback
  };


  // Initialize steps from initialSteps prop
  useEffect(() => {
    const currentInitialStepsKey = JSON.stringify(initialSteps);

    if (initialSteps && initialSteps.length > 0) {
      // Update if we have valid initialSteps and they've changed
      if (currentInitialStepsKey !== previousInitialStepsRef.current) {
        setSteps(initialSteps);
        isInitializedRef.current = true;
        previousInitialStepsRef.current = currentInitialStepsKey;
      }
    } else if (!isInitializedRef.current) {
      // Only reset to empty on first mount when initialSteps is empty/undefined
      // This handles Create mode where we start fresh
      setSteps([]);
      isInitializedRef.current = true;
      previousInitialStepsRef.current = currentInitialStepsKey;
    }
    // Don't reset when navigating between steps if we already have data
  }, [initialSteps]);

  // Call onSequenceUpdate whenever steps change
  // Use a ref to track the last steps sent to avoid infinite loops
  const lastStepsRef = useRef<string>("");

  useEffect(() => {
    const currentStepsKey = JSON.stringify(steps);

    if (
      onSequenceUpdate && steps.length >= 0 &&
      currentStepsKey !== lastStepsRef.current
    ) {
      lastStepsRef.current = currentStepsKey;
      onSequenceUpdate(steps);
    }
  }, [steps, onSequenceUpdate]);

  // Auto-scroll the canvas horizontally when dragging near its edges
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const EDGE_THRESHOLD = 100;
    const MAX_SCROLL_SPEED = 15;
    let scrollDirection = 0;
    let scrollSpeed = 0;

    const animateScroll = () => {
      if (scrollDirection !== 0 && canvas) {
        canvas.scrollLeft += scrollDirection * scrollSpeed;
        autoScrollAnimationRef.current = requestAnimationFrame(animateScroll);
      }
    };

    const handleDragOver = (e: globalThis.DragEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX;

      // Ignore if cursor is outside canvas bounds
      if (x < rect.left || x > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
        if (scrollDirection !== 0) {
          scrollDirection = 0;
          if (autoScrollAnimationRef.current) {
            cancelAnimationFrame(autoScrollAnimationRef.current);
            autoScrollAnimationRef.current = null;
          }
        }
        return;
      }

      const distFromLeft = x - rect.left;
      const distFromRight = rect.right - x;

      let newDirection = 0;
      let newSpeed = 0;

      if (distFromLeft < EDGE_THRESHOLD && canvas.scrollLeft > 0) {
        newDirection = -1;
        newSpeed = Math.max(2, MAX_SCROLL_SPEED * (1 - distFromLeft / EDGE_THRESHOLD));
      } else if (
        distFromRight < EDGE_THRESHOLD &&
        canvas.scrollLeft < canvas.scrollWidth - canvas.clientWidth
      ) {
        newDirection = 1;
        newSpeed = Math.max(2, MAX_SCROLL_SPEED * (1 - distFromRight / EDGE_THRESHOLD));
      }

      scrollSpeed = newSpeed;

      if (newDirection !== scrollDirection) {
        scrollDirection = newDirection;
        if (autoScrollAnimationRef.current) {
          cancelAnimationFrame(autoScrollAnimationRef.current);
          autoScrollAnimationRef.current = null;
        }
        if (scrollDirection !== 0) {
          autoScrollAnimationRef.current = requestAnimationFrame(animateScroll);
        }
      }
    };

    const stopAutoScroll = () => {
      scrollDirection = 0;
      scrollSpeed = 0;
      if (autoScrollAnimationRef.current) {
        cancelAnimationFrame(autoScrollAnimationRef.current);
        autoScrollAnimationRef.current = null;
      }
    };

    // Use capture phase so we see events before children call stopPropagation
    canvas.addEventListener("dragover", handleDragOver, true);
    document.addEventListener("dragend", stopAutoScroll);
    document.addEventListener("drop", stopAutoScroll);

    return () => {
      canvas.removeEventListener("dragover", handleDragOver, true);
      document.removeEventListener("dragend", stopAutoScroll);
      document.removeEventListener("drop", stopAutoScroll);
      stopAutoScroll();
    };
  }, [steps.length]);

  const handleDrop = useCallback((policy: Policy) => {
    stepCounterRef.current += 1;
    const newStep: AuditStep = {
      id: `step-${Date.now()}-${stepCounterRef.current}`,
      policies: [policy],
      responseCheck: false,
      // First step should always have preSendReview enabled
      preSendReview: false,
    };
    setSteps((prev) => {
      const newSteps = [...prev, newStep];
      // If this is the first step, ensure preSendReview is true
      if (newSteps.length === 1) {
        newSteps[0] = { ...newSteps[0], preSendReview: true };
      }
      return newSteps;
    });
  }, []);

  const handleDropAfterStep = useCallback(
    (stepIndex: number, policy: Policy) => {
      stepCounterRef.current += 1;
      const newStep: AuditStep = {
        id: `step-${Date.now()}-${stepCounterRef.current}`,
        policies: [policy],
        responseCheck: false,
        preSendReview: false,
      };
      setSteps((prev) => {
        const newSteps = [...prev];
        newSteps.splice(stepIndex + 1, 0, newStep);
        return newSteps;
      });
    },
    [],
  );

  const handleAddPolicyToStep = useCallback(
    (stepIndex: number, policy: Policy) => {
      setSteps((prev) => {
        // Check if policy is already used in any step
        const isPolicyUsed = prev.some((step) =>
          step.policies.some((p) => p.id === policy.id),
        );

        // If policy is already used, don't add it
        if (isPolicyUsed) {
          return prev;
        }

        return prev.map((step, i) =>
          i === stepIndex
            ? { ...step, policies: [...step.policies, policy] }
            : step,
        );
      });
    },
    [],
  );

  const handleRemovePolicy = useCallback(
    (stepIndex: number, policyId: string) => {
      setSteps((prev) => {
        const newSteps = prev.map((step, i) => {
          if (i === stepIndex) {
            return {
              ...step,
              policies: step.policies.filter((p) => p.id !== policyId),
            };
          }
          return step;
        });
        return newSteps.filter((step) => step.policies.length > 0);
      });
    },
    [],
  );

  const handleMove = useCallback((fromIndex: number, toIndex: number) => {
    setSteps((prev) => {
      const newSequence = [...prev];
      const [movedItem] = newSequence.splice(fromIndex, 1);
      newSequence.splice(toIndex, 0, movedItem);
      return newSequence;
    });
  }, []);

  const handleToggleResponseCheck = useCallback((index: number) => {
    setSteps((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, responseCheck: !item.responseCheck } : item,
      ),
    );
  }, []);

  const handleTogglePreSendReview = useCallback((index: number) => {
    // Prevent toggling preSendReview for the first step (index === 0)
    if (index === 0) return;

    setSteps((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, preSendReview: !item.preSendReview } : item,
      ),
    );
  }, []);

  const usedPolicyIds = new Set(
    steps.flatMap((step) => step.policies.map((p) => p.id)),
  );
  const allPoliciesSequenced = policies.length === usedPolicyIds.size;

  // Get available policies (not yet used)
  const availablePolicies = policies.filter(
    (policy) => !usedPolicyIds.has(policy.id),
  );

  const handleOpenModal = useCallback(
    (stepIndex: number, stepNumber: number) => {
      setModalStepIndex(stepIndex);
      setModalStepNumber(stepNumber);
      setShowModal(true);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalStepIndex(-1);
    setModalStepNumber(0);
  }, []);

  const handleSelectPolicyFromModal = useCallback(
    (policy: Policy) => {
      if (modalStepIndex >= 0) {
        handleDropAfterStep(modalStepIndex, policy);
      }
    },
    [modalStepIndex, handleDropAfterStep],
  );

  const handleCanvasDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    canvasDragCounter.current++;
    setIsCanvasOver(true);
  };

  const handleCanvasDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleCanvasDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    canvasDragCounter.current--;

    if (canvasDragCounter.current === 0) {
      setIsCanvasOver(false);
    }
  };

  const handleCanvasDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsCanvasOver(false);
    canvasDragCounter.current = 0;

    // Only allow drops on empty canvas (no steps yet)
    if (steps.length > 0) {
      return;
    }

    try {
      const jsonData = e.dataTransfer.getData("application/json");
      if (jsonData) {
        const data = JSON.parse(jsonData);
        if (data.type === "POLICY") {
          handleDrop(data.policy);
        }
      }
    } catch (err) {
      console.error("Drop error:", err);
    }
  };

  return (
    <div className="audit-sequencing-container">
      {/* Policy Selection Modal */}
      {showModal && (
        <PolicySelectionModal
          availablePolicies={availablePolicies}
          onSelectPolicy={handleSelectPolicyFromModal}
          onClose={handleCloseModal}
          stepNumber={modalStepNumber}
          renderIconByName={renderIconByName}
        />
      )}

      {/* Left Panel - Sequencing Library */}
      <div className="sequencing-library">
        <div className="library-header">
          <h4 className="library-title">Data Collection Stages</h4>
          <span className="library-badge">{policies.length} Stages</span>
        </div>

        {/* <div className="library-notice">
          <p className="library-notice-text">Each item can be used once</p>
        </div> */}

        <div className="library-policies">
          {policies.map((policy) => (
            <PolicyCard
              key={policy.id}
              policy={policy}
              isDisabled={usedPolicyIds.has(policy.id)}
              renderIconByName={renderIconByName}
            />
          ))}
        </div>
      </div>

      {/* Right Panel - Sequencing Flow */}
      <div className="sequencing-flow-panel">
        <div className="flow-header">
          <h4 className="flow-title">Define Data Collection Workflow</h4>
        </div>

        <div className="flow-content">
          <div
            ref={canvasRef}
            onDragEnter={handleCanvasDragEnter}
            onDragOver={handleCanvasDragOver}
            onDragLeave={handleCanvasDragLeave}
            onDrop={handleCanvasDrop}
            className={`flow-canvas ${steps.length > 0 ? "has-steps" : ""} ${isCanvasOver ? "drag-over" : ""}`}
          >
            {/* Purple Decoration */}
            {/* <div className="flow-decoration" /> */}

            {/* Completion Banner */}
            {allPoliciesSequenced && steps.length > 0 && (
              <div className="completion-banner">
                <CheckCircle />
                All data collection stages sequenced
              </div>
            )}

            {steps.length === 0 ? (
              /* Empty State */
              <div className="flow-controls">
                <button className="flow-btn">
                  <Play />
                  Start
                </button>

                <div className="flow-connector" />

                <div className="flow-empty-state">
                  <div className="flow-empty-icon">
                    <FileText />
                  </div>
                  <p className="flow-empty-text">
                    Drag stages from the left panel to define data collection workflow
                  </p>
                </div>

                <div className="flow-connector" />

                <button className="flow-btn">
                  <CheckCircle />
                  End
                </button>
              </div>
            ) : (
              /* Steps Container */
              <div className="steps-container">
                {steps.map((step, index) => (
                  <div key={step.id} className="sequence-step-wrapper">
                    <StepCard
                      step={step}
                      index={index}
                      stepNumber={index + 1}
                      onRemovePolicy={handleRemovePolicy}
                      onMove={handleMove}
                      onToggleResponseCheck={handleToggleResponseCheck}
                      onTogglePreSendReview={handleTogglePreSendReview}
                      onAddPolicyToStep={handleAddPolicyToStep}
                      hoverStepIndex={hoverStepIndex}
                      setHoverStepIndex={setHoverStepIndex}
                      onHeightChange={handleStepHeight}
                      renderIconByName={renderIconByName}
                    />
                    <StepDropZone
                      onDrop={(policy) => handleDropAfterStep(index, policy)}
                      onClickAddStep={() => handleOpenModal(index, index + 2)}
                      stepNumber={index + 2}
                      isLastDropZone={index === steps.length - 1}
                      allPoliciesUsed={allPoliciesSequenced}
                      hoverAddIndex={hoverAddIndex}
                      setHoverAddIndex={setHoverAddIndex}
                      height={stepHeights[0] || 180}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export function GreenPlaceholder({ label, height }: { label: string; height?: number }) {
  return (
    <div className="green-placeholder" style={{ height: height || 180 }}>
      <div className="green-plus">+</div>
      <p className="green-label">{label}</p>
    </div>
  );
}
// ===== EXPORT =====
export default AuditSequencing;

